import React, { useEffect, useMemo, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import Modal from '../../components/ui/Modal';
import Button from '../../components/ui/Button';
import { extractErrorMessage } from '../../api/client';
import ProcurementFormInner from './ProcurementFormInner';
import {
  createProcurement,
  createProcurementWithFile,
} from '../../api/procurement';

const EMPTY = {
  title: '',
  description: '',
  vendorId: null,
  projectId: '',
  quantity: 1,
  unitPrice: 0,
  amount: 0,
};

function toNumber(v) {
  if (v === '' || v === null || v === undefined) return 0;
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

export default function ProcurementForm({ open, onClose, onCreated }) {
  const qc = useQueryClient();
  const [form, setForm] = useState(EMPTY);
  const [file, setFile] = useState(null);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (open) {
      setForm(EMPTY);
      setFile(null);
      setErrors({});
    }
  }, [open]);

  const mutation = useMutation({
    mutationFn: async () => {
      const payload = {
        title: form.title.trim(),
        description: form.description?.trim() || null,
        amount: toNumber(form.amount),
        quantity: toNumber(form.quantity),
        unitPrice: toNumber(form.unitPrice),
        vendorId: form.vendorId || null,
        projectId: form.projectId ? Number(form.projectId) : null,
      };
      return file
        ? createProcurementWithFile(payload, file)
        : createProcurement(payload);
    },
    onSuccess: (data) => {
      toast.success('Procurement request created');
      qc.invalidateQueries({ queryKey: ['procurement'] });
      onCreated?.(data);
      onClose?.();
    },
    onError: (e) => toast.error(extractErrorMessage(e)),
  });

  function validate() {
    const errs = {};
    if (!form.title.trim()) errs.title = 'Title is required';
    if (toNumber(form.quantity) <= 0) errs.quantity = 'Quantity must be > 0';
    if (toNumber(form.unitPrice) < 0) errs.unitPrice = 'Unit price cannot be negative';
    if (toNumber(form.amount) <= 0) errs.amount = 'Amount must be > 0';
    if (!form.projectId) errs.projectId = 'Project is required';
    setErrors(errs);
    return Object.keys(errs).length === 0;
  }

  function handleSubmit(e) {
    e?.preventDefault?.();
    if (!validate()) return;
    mutation.mutate();
  }

  return (
    <Modal
      open={open}
      onClose={mutation.isPending ? undefined : onClose}
      title="New Procurement Request"
      description="Create a draft procurement request. You can submit it for approval afterwards."
      size="lg"
      footer={
        <>
          <Button
            variant="ghost"
            onClick={onClose}
            disabled={mutation.isPending}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleSubmit}
            loading={mutation.isPending}
          >
            Create Request
          </Button>
        </>
      }
    >
      <form
        onSubmit={handleSubmit}
        className="grid grid-cols-1 gap-4"
      >
        <ProcurementFormInner
          form={form}
          setForm={setForm}
          errors={errors}
          file={file}
          setFile={setFile}
        />
      </form>
    </Modal>
  );
}
